import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Icon } from './Icon'

/** Compact “i” — click/tap opens a real popup (title alone fails on many devices). */
export function FieldInfo({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const tipId = useId()

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent | TouchEvent) {
      const el = wrapRef.current
      if (el && e.target instanceof Node && !el.contains(e.target)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('touchstart', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('touchstart', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!text) return null
  return (
    <span className={`rd-field-info-wrap${open ? ' open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="rd-field-info"
        aria-label={text}
        aria-expanded={open}
        aria-controls={tipId}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen((v) => !v)
        }}
      >
        <Icon name="info" size={14} />
      </button>
      {open ? (
        <span id={tipId} className="rd-field-info-pop" role="tooltip">
          {text}
        </span>
      ) : null}
    </span>
  )
}

export function FieldLabel({
  htmlFor,
  children,
  info,
}: {
  htmlFor?: string
  children: ReactNode
  info?: string
}) {
  return (
    <div className="rd-field-label">
      <label className="rd-field-label-text" htmlFor={htmlFor}>
        {children}
      </label>
      {info ? <FieldInfo text={info} /> : null}
    </div>
  )
}
